import React from 'react'
import { useAppDispatch } from '../../../../../hooks/redux'

import TaskTableStateProvider, {
  getExpanded,
  calcID
} from '../../../../../providers/TaskTableStateProvider'

import SettingsEditProvider from '../../../../../providers/SettingsEditProvider'

import TaskTable from '../../../TaskTable'
import SettingsEditModal from '../../../../Settings/SettingsEditModal'

import config from '../../../../../config'
import { KEY_MAP } from '../../../../../providers/ApiProvider/helpers'
import { CATEGORY_TAB } from '../../../../../providers/TaskPageStateProvider'
import useAccess, { PERMS } from '../../../../../hooks/useAccess'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'
import { useTreeState, TYPES } from '../../../../../providers/TreeStateProvider'
import {
  useTaskAPI,
  CALLBACK_PHASES
} from '../../../../../providers/TaskAPIProvider'
import { useTaskSWRMutateCache } from '../../../../../providers/TaskSWRMutateCache'
import { useTaskComments } from '../../../../../providers/TaskCommentsProvider'
import { deleteCategory, updateCategory } from '../../../../../slices/category'
import useInfiniteFetch from '../../../../../hooks/useInfiniteFetch'
import useTaskIncludes from '../../useTaskIncludes'
import useStyles from './styles'

const getUrl = id => (page, per_page) => {
  const transformed = KEY_MAP.category(['category', `category_ids[]=${id}`])
  const fullUrl = `${config.apiBaseUri}${transformed}&page=${page}&per_page=${per_page}`
  return fullUrl
}

const CategoryTable = ({
  pageRef,
  category,
  isNone,
  createTask,
  setTaskModalCategory,
  hideHeader
}) => {
  const dispatch = useAppDispatch()
  const hasPermission = useAccess()
  const { user: currentUser } = useEnvironment()
  const { state: treeState } = useTreeState(TYPES.TASK)
  const { updateCacheFromSWR, registerCallback } = useTaskAPI()
  const { setMutator } = useTaskSWRMutateCache()
  const { updateCounts } = useTaskComments()

  const expanded = React.useMemo(
    () => getExpanded(treeState, isNone ? 'No Category' : category?.id),
    [treeState, isNone, category]
  )

  const canModify = React.useMemo(() => {
    if (hasPermission(PERMS.TasksAdmin)) {
      return true
    }

    if (hasPermission(PERMS.TasksOwn)) {
      return category?.owner_id === currentUser?.id
    }

    return false
  }, [hasPermission, category, currentUser])

  const [open, setOpen] = React.useState(false)

  // Flags for loading once only
  const [wasExpanded, setWasExpanded] = React.useState(expanded)

  React.useEffect(() => {
    if (expanded) {
      setWasExpanded(true)
    }
  }, [expanded])

  const categoryIncludes = useTaskIncludes({ key: calcID(category, isNone) })
  // Only fetch the category once when expanded and in view
  const {
    data: tasks,
    loaded,
    fullPagination,
    ...pagination
  } = useInfiniteFetch(() => {
    if (!wasExpanded) {
      return null
    }

    return `category:category_ids[]=${
      isNone ? false : category?.id ?? null
    }${categoryIncludes}`
  })

  const classes = useStyles({ loaded })

  // Store loaded tasks in internal cache
  React.useEffect(() => {
    updateCacheFromSWR(tasks)
  }, [updateCacheFromSWR, tasks])

  // Store comment counts
  React.useEffect(() => {
    if (tasks) {
      updateCounts(tasks.flatMap(x => x.data))
    }
  }, [updateCounts, tasks])

  // Update mutator
  React.useEffect(() => {
    setMutator(
      CATEGORY_TAB,
      isNone ? 'No Category' : category.id,
      pagination.mutate
    )
  }, [setMutator, isNone, category, pagination.mutate])

  // Register mutate function to be called when task update happens
  React.useEffect(() => {
    registerCallback(
      CALLBACK_PHASES.UPDATE,
      `CategoryTab-${isNone ? 'none' : category?.id ?? ''}`,
      pagination.mutate
    )
  }, [registerCallback, isNone, category, pagination.mutate])

  const handleDeleteCategory = React.useCallback(
    obj => {
      dispatch(deleteCategory(obj.id))
    },
    [dispatch]
  )

  const handleEditCategory = React.useCallback(
    async (obj, name) => {
      await dispatch(updateCategory({ ...obj, name }))
    },
    [dispatch]
  )

  const handleCreateCategoryTask = () => {
    if (setTaskModalCategory) {
      setTaskModalCategory(category?.id)
    }
    createTask()
  }

  const id = isNone ? 'No Category' : `table-${category?.id}`

  const paginationProp = React.useMemo(
    () => ({
      ...fullPagination,
      urlFunc: getUrl(isNone ? false : category?.id ?? null)
    }),
    [fullPagination, isNone, category]
  )

  return (
    <>
      <div className={classes.root}>
        <TaskTableStateProvider
          category={category}
          tasks={tasks}
          isNone={isNone}
          fullPagination={paginationProp}
        >
          <TaskTable
            allowDragDropSort
            dragDropID={isNone ? 'No Category' : category?.id}
            isInfinite={true}
            pageRef={pageRef}
            loaded={loaded}
            message='There are no tasks for this category'
            {...pagination}
            id={id}
            multiple
            disableSort={true}
            headerProps={{
              onEditClick: canModify
                ? () => {
                    setOpen(true)
                  }
                : undefined
            }}
            isPaginatedInfiniteLoader={true}
            createTask={handleCreateCategoryTask}
            hideHeader={hideHeader}
            showNoCategoryHeader={isNone && hideHeader}
            customFieldsToInclude={categoryIncludes}
          />
        </TaskTableStateProvider>
      </div>

      {open && (
        <SettingsEditProvider>
          <SettingsEditModal
            open
            onClose={() => setOpen(false)}
            obj={category}
            objType='category'
            superType='task'
            handleEdit={handleEditCategory}
            handleDelete={handleDeleteCategory}
          />
        </SettingsEditProvider>
      )}
    </>
  )
}

export default CategoryTable
